import { gql, useMutation } from '@apollo/client';
import { Button, ButtonGroup, Heading, StatusBadge, Text } from '@apollo/orbit';
import React, { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const sendTestNotificationMutation = gql`
  mutation SendTestNotificationMutation($variant: String!, $graphId: ID!) {
    graph(id: $graphId) {
      variant(name: $variant) {
        queueTestCustomChecksRequest {
          ... on GraphVariant {
            id
          }
          ... on PermissionError {
            message
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  }
`;

export const RegisteredWebhookSection = ({
  disableCardContent,
  endpointUrl,
  graphId,
  variant,
  secretTokenIsSet,
  customChecksEnabled,
}: {
  disableCardContent: boolean;
  endpointUrl: string | undefined;
  graphId: string;
  variant: string;
  secretTokenIsSet: boolean;
  customChecksEnabled: boolean;
}) => {
  const location = useLocation();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [sendTestNotification] = useMutation<
    GraphQLTypes.SendTestNotificationMutation,
    GraphQLTypes.SendTestNotificationMutationVariables
  >(sendTestNotificationMutation);
  const handleSendTestNotification = useCallback(async () => {
    setLoading(true);
    const { data, errors } = await sendTestNotification({
      variables: {
        graphId,
        variant,
      },
    });
    if (errors) {
      setError(errors[0]?.message);
    } else if (
      data?.graph?.variant?.queueTestCustomChecksRequest.__typename ===
      'PermissionError'
    ) {
      setError(data.graph.variant.queueTestCustomChecksRequest.message);
    } else if (
      data?.graph?.variant?.queueTestCustomChecksRequest.__typename ===
      'ValidationError'
    ) {
      setError(data.graph.variant.queueTestCustomChecksRequest.message);
    }
    setLoading(false);
  }, [graphId, sendTestNotification, variant]);

  return (
    <div className="flex flex-col">
      <div className="flex w-full flex-row justify-between">
        <div>
          <Heading as="h3">Registered webhook</Heading>
          <Text>Below are the details of your registered webhook.</Text>
        </div>
        <ButtonGroup>
          <Button
            variant="secondary"
            isLoading={loading}
            isDisabled={disableCardContent || !endpointUrl}
            onClick={handleSendTestNotification}
          >
            Send test notification
          </Button>
          <Button
            variant="secondary"
            aria-label="Edit webhook url"
            as={Link}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.editCustomChecksWebhook,
            })}
            isDisabled={disableCardContent}
          >
            Edit webhook
          </Button>
        </ButtonGroup>
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <div className="mt-4">
        <Heading as="h4" variant="title">
          Status
        </Heading>
        <StatusBadge
          variant="hidden"
          colorScheme={customChecksEnabled && endpointUrl ? 'green' : 'gray'}
        >
          {customChecksEnabled && endpointUrl ? 'Active' : 'Inactive'}
        </StatusBadge>
        <Heading className="mt-2" as="h4" variant="title">
          Endpoint URL
        </Heading>
        <Text>{endpointUrl ?? 'Unset'}</Text>
        <Heading className="mt-2" as="h4" variant="title">
          Secret token
        </Heading>
        <Text>{secretTokenIsSet ? '•••••••••••' : 'Unset'}</Text>
      </div>
    </div>
  );
};
